/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
  memo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { LoginAPI, LoginUserContext } from '@yoda/components-core';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RolesIes, tutoringAPILogin, TutoringAPILoginResponse } from '../../utils/Network';
import { AuthType, getIesLoginRedirectUrl, isAzureAuth, isIesAuth } from '../../utils/utilsts';
import { User } from '@yoda/components-core/lib/AzureLogin/types';

declare module '@yoda/components-core/lib/AzureLogin/types' {
  interface User {
    type?: AuthType.AZURE;
  }
}

export type UserIes = {
  type: AuthType.IES;
  username: string;
  name: string;
  email: string;
  token: {
    token: string;
    expiresAt: string;
  };
  roles: RolesIes[];
};

type DecodedJWTToken = {
  user: UserIes;
  iat: number;
  exp: number;
};

interface UserContextProps {
  user: any;
  init: boolean;
  azureAPI: any;
  hasRole: (role: string) => boolean;
  userToken: string | undefined;
  hasAnyRole: () => boolean;
  login: () => void;
  logout: () => void;
  error: boolean;
  loading: boolean;
  addAuthType: (authType: string) => void;
  removeAuthType: () => void;
  backToLogin: () => void;
}

const emptyContext: UserContextProps = {
  user: null,
  init: false,
  azureAPI: new LoginAPI(),
  userToken: '',
  hasRole: () => false,
  hasAnyRole: () => false,
  login: () => {},
  logout: () => {},
  error: false,
  loading: false,
  addAuthType: () => '',
  removeAuthType: () => '',
  backToLogin: () => {},
};

const UserContext = createContext(emptyContext);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = memo(({ children }: UserProviderProps) => {
  const [user, setUser] = useState<User | UserIes | null>(null);
  const [error, setError] = useState(false);
  const [init, setInit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [azureAPI] = useState(() => new LoginAPI());

  const addAuthType = (authType: string) => sessionStorage.setItem('authType', authType);

  const removeAuthType = () => {
    const authType = sessionStorage.getItem('authType');
    if (authType) {
      sessionStorage.removeItem('authType');
    }
  };

  const navigate = useNavigate();

  const hasUseEffectExecuted = useRef(false);

  const [searchParams] = useSearchParams();

  const resetToken = useCallback(() => {
    sessionStorage.removeItem('session_token');
    sessionStorage.removeItem('user_info');
    sessionStorage.removeItem('authType');
    setUser(null);
  }, []);

  const backToLogin = useCallback(() => {
    resetToken();
    navigate('/');
  }, [navigate, resetToken]);

  const saveUserInfo = ({
    email,
    name,
    roles,
    username,
    refreshToken,
    token,
  }: TutoringAPILoginResponse) => {
    const userInfo = {
      name,
      username,
      email,
      roles,
      token,
      refreshToken,
      type: AuthType.IES,
    };

    setUser(userInfo as UserIes);
    sessionStorage.setItem('user_info', JSON.stringify(userInfo));
  };

  const saveAuthToken = useCallback(
    (token: string, userInfo?: TutoringAPILoginResponse) => {
      try {
        const decodedJWTToken = jwtDecode<DecodedJWTToken>(token);
        if (decodedJWTToken.exp * 1000 > Date.now()) {
          sessionStorage.setItem('session_token', token);
          if (userInfo) {
            saveUserInfo(userInfo);
          }
          setInit(true);
          addAuthType(AuthType.IES);
        } else {
          console.info('Jwt expired');
          sessionStorage.removeItem('authType');
          resetToken();
          navigate('/login');
        }
      } catch (error) {
        console.error('Invalid token found', error);
        resetToken();
      }
    },
    [resetToken, navigate],
  );

  useEffect(() => {
    if (isIesAuth()) {
      const code = searchParams.get('code');
      const clientId = searchParams.get('client_id');
      const state = searchParams.get('state');
      const stateFromStorage = sessionStorage.getItem('authState');

      if (hasUseEffectExecuted.current) {
        return;
      }

      hasUseEffectExecuted.current = true;
      const initialize = async () => {
        // SCENARIO 1: Handle the OAuth callback
        if (code && clientId) {
          try {
            if (state !== stateFromStorage) {
              setError(true);
              setInit(true);
              setLoading(false);
              sessionStorage.removeItem('authType');
              return navigate('/login', { replace: true });
            }
            setLoading(true);
            const loginResponse = await tutoringAPILogin(code, clientId);
            addAuthType(AuthType.IES);
            saveAuthToken(loginResponse.token.token, loginResponse);
            navigate('/', { replace: true });
            sessionStorage.removeItem('authState');
          } catch (e) {
            console.error('Error during OAuth callback', e);
            setError(true);
            setInit(true);
            setLoading(false);
            sessionStorage.removeItem('authType');
            navigate('/login', { replace: true });
          }
        } else {
          // SCENARIO 2: Check for an existing session token
          const localToken = sessionStorage.getItem('session_token');
          const userInfo = sessionStorage.getItem('user_info');

          if (localToken && userInfo) {
            const userInfoParsed = JSON.parse(userInfo) as TutoringAPILoginResponse;
            saveAuthToken(localToken, userInfoParsed);
          } else {
            setInit(true);
            resetToken();
            navigate('/login');
          }
        }
        setLoading(false);
      };

      initialize();
    }
  }, [saveAuthToken, searchParams, navigate, resetToken]);

  useEffect(() => {
    // First setInit
    if (!isAzureAuth() && !isIesAuth()) {
      return setInit(true);
    }
    if (isAzureAuth()) {
      azureAPI.getLoggedInUser((user: any) => {
        if (user) {
          setInit(true);
          setUser({ ...user, type: AuthType.AZURE });
          return addAuthType(AuthType.AZURE);
        }

        removeAuthType()
      });
    }
  }, [azureAPI]);

  const roles = useMemo(() => {
    if (!user) return [];

    if (user.type === AuthType.IES) {
      const rolesUsersIes = user.roles || [];
      return rolesUsersIes.map((r) => r.toUpperCase());
    }

    const rolesUserAzure = user.idTokenClaims?.roles || [];

    return rolesUserAzure.map((r) => r.toUpperCase());
  }, [user]);

  const userToken = useMemo(() => {
    if (!user) return '';
    if (user.type === AuthType.IES) {
      return user.token.token;
    }
    return user.token;
  }, [user]);

  const getUserToken = useCallback(() => {
    return new Promise<string>((_resolve, reject) => {
      new LoginAPI().getLoggedInUser((loggedInUser: User) => {
        if (loggedInUser) {
          setUser({ ...loggedInUser, type: AuthType.AZURE });
        } else {
          reject({ message: 'user is not logged in...' });
        }
      });
    });
  }, []);

  useEffect(() => {
    const intevelId = setInterval(() => {
      const localToken = sessionStorage.getItem('session_token');
      if (isIesAuth() && localToken) {
        return saveAuthToken(localToken);
      }
      getUserToken();
    }, 1000 * 60 * 15);
    return () => clearInterval(intevelId);
  }, []);

  const hasRole = useCallback(
    (role: string) => {
      if (!role) return false;
      return roles.includes(role);
    },
    [roles],
  );

  const login = useCallback(() => {
    try {
      const state = window.crypto.randomUUID();
      sessionStorage.setItem('authState', state);
      window.location.href = getIesLoginRedirectUrl(state);
    } catch (err) {
      setError(true);
      console.error(err);
    }
  }, []);

  const value = useMemo(() => {
    const updatedUser =
      user && user.type === AuthType.AZURE
        ? {
            ...user,
            refreshTokenCallback: (_unusedUserArg: any, callback: any) => {
              // NOTE: `_unusedUserArg` has modified `refreshTokenCallback`, do not use it here
              user.refreshTokenCallback?.(user, (token: string) => {
                setUser({ ...user, token });
                callback(token);
              });
            },
          }
        : null;

    return {
      user: updatedUser ?? user,
      init,
      azureAPI,
      userToken,
      hasRole,
      hasAnyRole: () => !!roles.length,
      error,
      loading,
      login,
      logout: () => {
        azureAPI.logout().catch(console.error);
      },
      addAuthType,
      removeAuthType,
      backToLogin,
    };
  }, [user, roles, hasRole, azureAPI, userToken, init, login, error, loading, backToLogin]);

  return (
    <UserContext.Provider value={value}>
      <LoginUserContext.Provider
        value={{
          user: user as User,
          setUser: setUser as React.Dispatch<React.SetStateAction<User | null>> | null,
        }}
      >
        {children}
      </LoginUserContext.Provider>
    </UserContext.Provider>
  );
});

// fix for eslint:
UserProvider.displayName = 'UserProvider';

export const useUserContext = () => useContext(UserContext);
